import {
  useEffect,
  useState,
  useCallback,
  createContext,
  useContext,
  JSX,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useHistory } from 'react-router-dom';
import { getIfIExist } from '../../repository/UserDAO';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { SafeguardRoles } from '../../components/routing/SafeguardRoutes/AcceptedSafeguardRoles';

import { useConnectivity } from '../connectivity_context/ConnectivityProvider';
import { useApiStore } from '@/stores/apiStore';
import {
  instanceOfMiniOrganization,
  MiniOrganization,
  OrganizationOnAzure,
} from '../../../generated-client/models';
import { getMiniOrganization } from '@/repository/OrganizationDAO';
import { Entitlements, LEAST_PRIVILEGE, deriveEntitlements } from '@/utils/entitlements/entitlements';

export const UserContext = createContext<UserContextType>({
  bearerToken: '',
  doIExist: async () => {},
  entitlements: LEAST_PRIVILEGE,
  isSafeguardAdmin: false,
  isSafeguardDev: false,
  isSafeguardManufacturingUser: false,
  refreshEntitlements: async (
    _?: MiniOrganization | OrganizationOnAzure,
  ) => {},
  safeguardView: false,
  setSafeguardView: (_: boolean) => {},
  userExists: false,
});

type SafeguardJWTPayload = JwtPayload & {
  permissions: string[];
};

export const UserProvider = ({ children }: { children: JSX.Element }) => {
  const [bearerToken, setBearerToken] = useState<string>('');
  const [userExists, setUserExists] = useState<boolean>(true);
  const { isOnlineRef } = useConnectivity();
  const { getAccessTokenSilently, user: authUser } = useAuth0();
  const [isSafeguardAdmin, setIsSafeguardAdmin] = useState(false);
  const [isSafeguardDev, setIsSafeguardDev] = useState(false);
  const [safeguardView, setSafeguardView] = useState(false);
  const [isSafeguardManufacturingUser, setIsSafeguardManufacturingUser] =
    useState(false);
  const [entitlements, setEntitlements] = useState<Entitlements>(
    LEAST_PRIVILEGE,
  );
  const history = useHistory();

  const setApiToken = useApiStore((s) => s.setToken);

  useEffect(() => {
    if (authUser && isOnlineRef.current) {
      (async () => {
        const token = await getAccessTokenSilently();
        setBearerToken(token);

        setApiToken(token);

        const decodedToken = jwtDecode<SafeguardJWTPayload>(token);

        const hasDevAccess = decodedToken.permissions.includes(
          SafeguardRoles.SAFEGUARD_DEV,
        );
        const hasAdminWrite = decodedToken.permissions.includes(
          SafeguardRoles.SAFEGUARD_ADMIN_WRITE,
        );
        const hasAdminRead = decodedToken.permissions.includes(
          SafeguardRoles.SAFEGUARD_ADMIN_READ,
        );
        const hasManufacturing = decodedToken.permissions.includes(
          SafeguardRoles.SAFEGUARD_MANUFACTURING,
        );
        setIsSafeguardDev(hasDevAccess);
        setIsSafeguardAdmin(hasAdminWrite);
        setIsSafeguardManufacturingUser(hasManufacturing && hasAdminRead);

        // Set safeguard view if user has any elevated permissions
        const hasElevatedAccess =
          hasDevAccess ||
          hasAdminWrite ||
          hasAdminRead ||
          (hasManufacturing && hasAdminRead);

        if (import.meta.env.VITE_DEFAULT_TO_USER_VIEW === 'true') {
          console.log(
            'Defaulting to user view due to environment variable setting.',
          );
          setSafeguardView(false);
        } else {
          setSafeguardView(hasElevatedAccess);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, isOnlineRef]);

  const doIExist = async () => {
    try {
      const userExists = await getIfIExist();

      setUserExists(userExists !== 'false');
    } catch (error) {
      console.log(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  /**
   * Recomputes the caller's tier/capability entitlements from an org update.
   * If we're already handed a MiniOrganization (it carries the caller's own
   * capability flags), derive directly from it. Otherwise (e.g. an admin
   * full-org update) we only have the org's identity, so re-fetch the mini
   * representation to get the capability flags for the current caller.
   */
  const refreshEntitlements = useCallback(
    async (org?: MiniOrganization | OrganizationOnAzure) => {
      if (!org) return;
      if (instanceOfMiniOrganization(org)) {
        setEntitlements(deriveEntitlements(org));
        return;
      }
      try {
        const mini = await getMiniOrganization(org.org_code);
        setEntitlements(deriveEntitlements(mini));
      } catch (e) {
        console.warn('Error fetching organization');
      }
    },
    [],
  );

  useEffect(() => {
    if (bearerToken) doIExist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearerToken]);

  // Handling token refresh
  useEffect(() => {
    setApiToken(bearerToken ?? null);
  }, [bearerToken, setApiToken]);

  useEffect(() => {
    if (userExists) history.push('/');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userExists]);

  return (
    <UserContext.Provider
      value={{
        bearerToken,
        userExists,
        doIExist,
        isSafeguardAdmin,
        isSafeguardDev,
        isSafeguardManufacturingUser,
        setSafeguardView,
        safeguardView,
        entitlements,
        refreshEntitlements,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export type UserContextType = {
  bearerToken: string;
  userExists: boolean;
  doIExist: () => Promise<void>;
  isSafeguardAdmin: boolean;
  isSafeguardDev: boolean;
  isSafeguardManufacturingUser: boolean;
  setSafeguardView: (value: boolean) => void;
  safeguardView: boolean;
  entitlements: Entitlements;
  refreshEntitlements: (
    organization?: MiniOrganization | OrganizationOnAzure,
  ) => Promise<void>;
};

/** @returns {UserContextType} */
export const useUserContext = (): UserContextType => {
  return useContext(UserContext);
};

export const useEntitlements = (): Entitlements =>
  useUserContext().entitlements;
